import { useState } from 'react'
import { Bell, Bot, Contact, Users2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { UserAvatarMenu } from './UserAvatarMenu'
import { ThemeSwitcher } from '@/components/ui/theme-switcher'
import { DensityToggle } from '@/components/DensityToggle'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { TranslationToggle } from '@/components/TranslationToggle'
import { TimezoneDisplayToggle } from '@/components/TimezoneDisplayToggle'
import { ContactsPanel } from './ContactsPanel'
import { OnlineUsersPanel } from './OnlineUsersPanel'
import { Logo } from '@/components/Logo'
import { useUser } from '@/app/store/auth'
import { useTimezoneDisplay } from '@/contexts/TimezoneDisplayContext'
import { useAiChatPanel } from '@/contexts/AiChatPanelContext'
import { usePresence } from '@/contexts/PresenceContext'
import { SupportDropdown } from './SupportDropdown'

export function Topbar() {
    const user = useUser()
    const { toggle: toggleAiChat, isOpen: aiChatOpen } = useAiChatPanel()
    const { displayMode: timezoneDisplayMode, setDisplayMode: setTimezoneDisplayMode } = useTimezoneDisplay()
    const [contactsOpen, setContactsOpen] = useState(false)
    const [onlineUsersOpen, setOnlineUsersOpen] = useState(false)
    const [supportOpen, setSupportOpen] = useState(false)
    const { onlineUsers } = usePresence()
    const onlineCount = onlineUsers.size

    if (!user) {
        return null
    }

    return (
        <header className="flex items-center justify-between border-b bg-background px-6 h-[var(--topbar-height)]">
            {/* Logo — click to open support dropdown */}
            <div className="flex-shrink-0 mr-6 cursor-pointer" onClick={() => setSupportOpen(!supportOpen)} title="VaultSentinel Support">
                <Logo />
            </div>

            {/* Right side */}
            <div className="flex items-center space-x-4">
                {/* AI Assistant - toggles slide-in panel */}
                <Button
                    variant={aiChatOpen ? 'secondary' : 'ghost'}
                    size="icon"
                    title="AI Assistant"
                    onClick={toggleAiChat}
                >
                    <Bot className="h-5 w-5" />
                </Button>

                {/* Notifications */}
                <Button variant="ghost" size="icon" className="relative">
                    <Bell className="h-5 w-5" />
                    <span className="absolute top-0.5 right-0.5 h-2.5 w-2.5 rounded-full bg-destructive text-[8px] text-destructive-foreground flex items-center justify-center leading-none">
                        3
                    </span>
                </Button>

                {/* Online Users */}
                <Button
                    variant={onlineUsersOpen ? 'secondary' : 'ghost'}
                    size="icon"
                    title="Online Users"
                    className="relative"
                    onClick={() => { setOnlineUsersOpen(!onlineUsersOpen); setContactsOpen(false) }}
                >
                    <Users2 className="h-5 w-5" />
                    {onlineCount > 0 && (
                        <span className="absolute top-0.5 right-0.5 h-2.5 w-2.5 rounded-full bg-green-500 text-[8px] text-white flex items-center justify-center leading-none font-medium">
                            {onlineCount > 9 ? '9+' : onlineCount}
                        </span>
                    )}
                </Button>

                {/* Contacts */}
                <Button
                    variant={contactsOpen ? 'secondary' : 'ghost'}
                    size="icon"
                    title="Contacts"
                    onClick={() => { setContactsOpen(!contactsOpen); setOnlineUsersOpen(false) }}
                >
                    <Contact className="h-5 w-5" />
                </Button>

                {/* Language switcher */}
                <LanguageSwitcher />

                {/* Translation toggle */}
                <TranslationToggle />

                {/* Timezone display toggle */}
                <TimezoneDisplayToggle
                    displayMode={timezoneDisplayMode}
                    onDisplayModeChange={setTimezoneDisplayMode}
                />

                {/* Density toggle */}
                <DensityToggle />

                {/* Theme switcher */}
                <ThemeSwitcher />

                {/* User menu */}
                <UserAvatarMenu user={user} />
            </div>

            {/* Support dropdown */}
            <SupportDropdown open={supportOpen} onClose={() => setSupportOpen(false)} />

            {/* Online Users slide-in panel */}
            <OnlineUsersPanel open={onlineUsersOpen} onClose={() => setOnlineUsersOpen(false)} />

            {/* Contacts slide-in panel */}
            <ContactsPanel open={contactsOpen} onClose={() => setContactsOpen(false)} />
        </header>
    )
}
